import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  Analytics,
  getAnalytics,
  provideAnalytics,
  ScreenTrackingService,
  UserTrackingService,
} from '@fido/firebase/analytics';
import { initializeApp, provideFirebaseApp } from '@fido/firebase/app';
import {
  initializeAppCheck,
  provideAppCheck,
  ReCaptchaEnterpriseProvider,
} from '@fido/firebase/app-check';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@fido/firebase/firestore';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@fido/firebase/functions';
import { getPerformance, Performance, providePerformance } from '@fido/firebase/performance';
import { connectStorageEmulator, getStorage, provideStorage } from '@fido/firebase/storage';
import { provideRouter, withViewTransitions } from '@angular/router';
import { FingerprintjsProAngularModule } from '@fingerprintjs/fingerprintjs-pro-angular';
import { provideLocalStorageImpl } from 'ngxtension/inject-local-storage';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { sessionExpiryInterceptor } from './session-expiry-interceptor';
import { provideResizeNotificationsForEmbedded } from './utils/iframe';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withViewTransitions()),
    provideHttpClient(withInterceptors([sessionExpiryInterceptor])),
    provideFirebaseApp(() => initializeApp(environment.firebase.options)),
    provideAnalytics(() => getAnalytics()),
    ScreenTrackingService,
    UserTrackingService,
    provideEnvironmentInitializer(() => inject(Analytics)),
    providePerformance(() => getPerformance()),
    provideEnvironmentInitializer(() => inject(Performance)),
    provideAppCheck(() =>
      initializeAppCheck(undefined, {
        provider: new ReCaptchaEnterpriseProvider(environment.firebase.appCheck.siteKey),
        isTokenAutoRefreshEnabled: true,
      }),
    ),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (isDevMode()) connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
      return firestore;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (isDevMode()) connectStorageEmulator(storage, '127.0.0.1', 9199);
      return storage;
    }),
    provideFunctions(() => {
      const functions = getFunctions();
      if (isDevMode()) connectFunctionsEmulator(functions, '127.0.0.1', 5001);
      return functions;
    }),

    importProvidersFrom(
      FingerprintjsProAngularModule.forRoot({
        loadOptions: {
          apiKey: environment.fingerprintjs.apiKey,
          endpoint: 'https://fingerprintjs.firmfirst.com',
          scriptUrlPattern:
            'https://fingerprintjs.firmfirst.com/web/v<version>/<apiKey>/loader_v<loaderVersion>.js',
        },
      }),
    ),

    provideLocalStorageImpl(sessionStorage),

    provideResizeNotificationsForEmbedded(),
  ],
};
